import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Radio,
  RadioGroup,
  Text,
} from "@chakra-ui/react";
import { FormikProps } from "formik";
import { RadioGroupControl } from "formik-chakra-ui";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import {
  SCHOOL_CONFLICT_SUMMARY,
  useFieldError,
  useSchoolErrorById,
} from "src/scenes/orgAdmin/formTemplates/RulesTab/FormEngineRuleDrawer/context";
import { FormEngineRuleFormData } from "src/scenes/orgAdmin/formTemplates/RulesTab/FormEngineRuleDrawer/schemas";
import {
  MultiSelectItem,
  School,
} from "src/scenes/orgAdmin/formTemplates/RulesTab/FormEngineRuleDrawer/shared";

type GeographicPreferenceConfigurationFormProps = {
  formik: FormikProps<FormEngineRuleFormData>;
  boundaries: { id: string; name: string | null }[];
  schools: School[];
};

export const GeographicPreferenceConfigurationForm: React.FC<
  GeographicPreferenceConfigurationFormProps
> = ({ formik, boundaries, schools }) => {
  const schoolErrorById = useSchoolErrorById();
  const boundaryIdsError = useFieldError("boundary_ids");
  const boundaryOptions = boundaries
    .filter((b) => b.name)
    .map((b) => ({ id: b.id, label: b.name ?? "" }));
  const InsideOutside: React.FC<{
    formik: FormikProps<FormEngineRuleFormData>;
  }> = ({ formik }) => {
    return (
      <RadioGroupControl name="inside_outside">
        <Flex gap="1.5rem" pl="1.5rem" mt="0.5rem">
          <Flex align="center" gap="0.5rem">
            <Radio value="inside" />
            <Text>Inside</Text>
          </Flex>
          <Flex align="center" gap="0.5rem">
            <Radio value="outside" />
            <Text>Outside</Text>
          </Flex>
        </Flex>
      </RadioGroupControl>
    );
  };
  return (
    <Flex direction="column" gap="1.5rem">
      <FormControl>
        <FormLabel pb="0.5rem" m="0">
          Which boundary should this apply to?
        </FormLabel>
        <RadioGroup
          value={formik.values.boundary_type ?? undefined}
          onChange={(value) => {
            formik.setFieldValue("inside_outside", null);
            formik.setFieldValue("boundary_type", value);
          }}
        >
          <Flex direction="column" gap="1rem">
            <Box>
              <Flex align="center" gap="0.5rem" mb="0.25rem">
                <Radio value="all" />
                <Text fontWeight="500">
                  Inside or outside selected school boundaries
                </Text>
              </Flex>
              <Text fontSize="sm" color="gray.600" pl="1.5rem">
                When student address falls within or outside any of their
                selected school boundaries set up in the enrollment period
              </Text>
              {formik.values.boundary_type === "all" && (
                <InsideOutside formik={formik} />
              )}
            </Box>
            <Box>
              <Flex align="center" gap="0.5rem" mb="0.25rem">
                <Radio value="selected" />
                <Text fontWeight="500">
                  Inside or outside specific boundaries
                </Text>
              </Flex>
              <Text fontSize="sm" color="gray.600" pl="1.5rem">
                When student address falls is inside or outside a specific
                boundary
              </Text>
              {formik.values.boundary_type === "selected" &&
                boundaryOptions.length === 0 && (
                  <Flex pl="1.5rem" mt="0.5rem">
                    <FormControl isInvalid>
                      <FormErrorMessage m="0">
                        Boundaries are not configured for this organization.
                      </FormErrorMessage>
                    </FormControl>
                  </Flex>
                )}
              {formik.values.boundary_type === "selected" &&
                boundaryOptions.length > 0 && (
                  <Flex gap="0.5rem" direction="column">
                    <InsideOutside formik={formik} />
                    <MultiSelectInput<MultiSelectItem>
                      size="md"
                      options={boundaryOptions}
                      initialValues={formik.values.boundary_ids.map((id) => ({
                        id: id,
                        label:
                          boundaries.find((b) => id === b.id)?.name ??
                          "<UNKNOWN>",
                      }))}
                      onChange={(selectedItems) => {
                        formik.setFieldValue(
                          "boundary_ids",
                          selectedItems.map((x) => x.id)
                        );
                      }}
                      placeholder="Select one or multiple boundaries"
                      validateSelectedItems={true}
                      invalidSelectionErrorMessage="Selected boundary is no longer available"
                      error={boundaryIdsError}
                      renderDropdownItem={(item) => (
                        <Flex key={item.id}>{item.label}</Flex>
                      )}
                    />
                    <FormControl>
                      <FormLabel pb="0.325rem" m="0">
                        Which schools should this rule apply to?
                      </FormLabel>
                      <MultiSelectInput
                        size="md"
                        options={schools.map((school) => ({
                          id: school.id,
                          label: school.name,
                        }))}
                        initialValues={formik.values.school_ids.map(
                          (schoolId) => {
                            const school = schools.find(
                              (s) => s.id === schoolId
                            );
                            return {
                              id: schoolId,
                              label: school ? school.name : schoolId,
                            };
                          }
                        )}
                        onChange={(selectedSchools) => {
                          formik.setFieldValue(
                            "school_ids",
                            selectedSchools.map((s) => s.id)
                          );
                        }}
                        placeholder="Select multiple"
                        error={
                          schoolErrorById.size > 0
                            ? SCHOOL_CONFLICT_SUMMARY
                            : undefined
                        }
                        isInvalidItem={(item) => schoolErrorById.has(item.id)}
                        getInvalidItemMessage={(item) =>
                          schoolErrorById.get(item.id)
                        }
                        renderDropdownItem={(item) => (
                          <Flex key={item.id}>{item.label}</Flex>
                        )}
                      />
                    </FormControl>
                    <Text fontSize="sm" color="gray.600">
                      The rule will apply only to the selected schools. If no
                      schools are selected, it will apply to all schools.
                    </Text>
                  </Flex>
                )}
            </Box>
          </Flex>
        </RadioGroup>
      </FormControl>
    </Flex>
  );
};
